import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { CertificateDownloadService } from '../../core/services/certificate-download.service';
import { NotificationService } from '../../services/notification.service';
import { UiHelperService } from '../../services/ui-helper.service';

interface JoinKeySlot {
  id: 'keyfile' | 'cerfile';
  name: string;
  title: string;
  subtitle: string;
  accept: string;
  fondo: string;
  file?: string;
}

@Component({
  selector: 'app-admin-join-keys',
  templateUrl: './admin-join-keys.component.html',
  styleUrls: ['./admin-join-keys.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule]
})
export class AdminJoinKeysComponent implements OnInit {
  private static readonly PFX_PASS_UPPER = /[A-Z]/;
  private static readonly PFX_PASS_LOWER = /[a-z]/;
  private static readonly PFX_PASS_DIGIT = /[0-9]/;
  private static readonly PFX_PASS_SPECIAL = /[$@$!%*?&_-]/;

  formaForm!: FormGroup;
  buttons: JoinKeySlot[] = [];
  validateformats: boolean[] = [];
  error: string[] = [];
  totalCantidad = 2;

  panelPfxListo = false;
  pfxDescargaDisparada = false;
  peekContrasena = false;
  peekContrasenaConfirmacion = false;
  generando = false;

  certificado: {
    mensaje?: string;
    nombreArchivo?: string;
    certificadoB64?: string;
  } | null = null;

  constructor(
    private fb: FormBuilder,
    private downloadService: CertificateDownloadService,
    private uiHelper: UiHelperService,
    private notification: NotificationService,
    private router: Router
  ) {
    this.formaForm = this.fb.group({
      contrasena: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(12),
          Validators.pattern(
            '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&_-])[A-Za-zd$@$!%*?&_-].{8,12}'
          )
        ]
      ],
      contrasenaConfirmacion: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(12),
          Validators.pattern(
            '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&_-])[A-Za-zd$@$!%*?&_-].{8,12}'
          )
        ]
      ],
      documentos: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.createSlot('Llave privada (.key)', 'keyfile');
    this.createSlot('Llave pública (.cer)', 'cerfile');
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(e: BeforeUnloadEvent): void {
    if (this.panelPfxListo && !this.pfxDescargaDisparada) {
      e.preventDefault();
      e.returnValue = '';
    }
  }

  get documentsDocuments(): FormArray {
    return this.formaForm.get('documentos') as FormArray;
  }

  /** Paso guía: archivos → contraseña → listo. */
  get guideStep(): 'archivos' | 'contrasena' | 'listo' {
    if (this.panelPfxListo) {
      return 'listo';
    }
    if (this.pfxArchivosListosCount < this.totalCantidad) {
      return 'archivos';
    }
    if (!this.puedeGenerarCertificado) {
      return 'contrasena';
    }
    return 'listo';
  }

  get passwordInputValue(): string {
    return String(this.formaForm.get('contrasena')?.value ?? '');
  }

  get passwordHasInput(): boolean {
    return this.passwordInputValue.length > 0;
  }

  get passwordMinMaxLengthValid(): boolean {
    const ctrl = this.formaForm.controls['contrasena'];
    return (
      !(ctrl.hasError('maxlength') || ctrl.hasError('minlength')) &&
      (ctrl?.value?.length ?? 0) > 0
    );
  }

  get passwordRuleStates(): { id: string; label: string; met: boolean }[] {
    const v = this.passwordInputValue;
    return [
      { id: 'upper', label: 'Mínimo una mayúscula', met: AdminJoinKeysComponent.PFX_PASS_UPPER.test(v) },
      { id: 'lower', label: 'Mínimo una minúscula', met: AdminJoinKeysComponent.PFX_PASS_LOWER.test(v) },
      { id: 'digit', label: 'Incluya mínimo un número', met: AdminJoinKeysComponent.PFX_PASS_DIGIT.test(v) },
      {
        id: 'special',
        label: 'Un carácter especial $@$!%*?&_-',
        met: AdminJoinKeysComponent.PFX_PASS_SPECIAL.test(v)
      },
      { id: 'length', label: 'Entre 8 y 12 caracteres', met: this.passwordMinMaxLengthValid }
    ];
  }

  get passwordRulesMetCount(): number {
    return this.passwordRuleStates.filter((r) => r.met).length;
  }

  get passwordRulesProgressPercent(): number {
    return (this.passwordRulesMetCount / 5) * 100;
  }

  get passwordRulesSummaryLabel(): string {
    if (!this.passwordHasInput) {
      return 'Escribe una contraseña para ver el avance';
    }
    if (this.passwordRulesMetCount === 5) {
      return 'Todos los requisitos cumplidos';
    }
    return `${this.passwordRulesMetCount} de 5 requisitos cumplidos`;
  }

  get contrasenaIguales(): boolean {
    return (
      this.formaForm.get('contrasena')!.value ===
      this.formaForm.get('contrasenaConfirmacion')!.value
    );
  }

  get pfxArchivosListosCount(): number {
    let c = 0;
    for (let i = 0; i < this.buttons.length; i++) {
      if (this.pfxDocumentoListo(i)) {
        c++;
      }
    }
    return c;
  }

  get puedeGenerarCertificado(): boolean {
    if (this.pfxArchivosListosCount !== this.totalCantidad) {
      return false;
    }
    if (this.validateformats?.some((v) => !!v)) {
      return false;
    }
    if (this.passwordRulesMetCount !== 5) {
      return false;
    }
    return this.contrasenaIguales;
  }

  pfxDocumentoListo(index: number): boolean {
    const b = this.buttons[index];
    return !!b && b.title === 'Listo' && !this.validateformats[index];
  }

  camposvalidos(campo: string): boolean {
    const c = this.formaForm.get(campo);
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  isValidFieldInArray(formArray: FormArray, index: number): boolean {
    return !!(formArray.controls[index]?.errors && formArray.controls[index]?.touched);
  }

  onSubmit(): void {
    if (!this.puedeGenerarCertificado) {
      this.formaForm.markAllAsTouched();
      return;
    }
    if (this.formaForm.invalid) {
      this.formaForm.markAllAsTouched();
      return;
    }
    const contrasena = String(this.formaForm.value.contrasena ?? '');
    this.unirLlaves(contrasena);
  }

  unirLlaves(contrasena: string): void {
    const key = this.buttons.find((b) => b.id === 'keyfile')?.file;
    const crt = this.buttons.find((b) => b.id === 'cerfile')?.file;
    if (!key || !crt) {
      return;
    }
    this.generando = true;
    this.uiHelper.showSpinner('Procesando petición, por favor espera un momento', 'auth');
    this.downloadService.unirLlaves({ crt, key, password: contrasena }).subscribe({
      next: (res) => {
        this.generando = false;
        this.uiHelper.hideSpinner();
        this.certificado = {
          mensaje: 'Solicitud procesada exitosamente',
          nombreArchivo: 'certificado_digital.pfx',
          certificadoB64: res?.pfx
        };
        this.panelPfxListo = true;
        this.pfxDescargaDisparada = false;
        queueMicrotask(() => this.descargarPfxGenerado());
      },
      error: (error) => {
        const msg = String(error?.error?.message ?? error?.message ?? '').toLowerCase();
        if (msg.includes('timed out')) {
          this.unirLlaves(contrasena);
          return;
        }
        this.generando = false;
        this.uiHelper.hideSpinner();
        const mensaje =
          error?.error?.mensaje ||
          (Array.isArray(error?.error?.listaErrores)
            ? error.error.listaErrores
                .map((o: { campo?: string; mensaje?: string }) =>
                  `${o.campo ?? ''}: ${o.mensaje ?? ''}`.trim()
                )
                .join('\n')
            : null) ||
          (Array.isArray(error?.error?.descripcionErrores)
            ? error.error.descripcionErrores.join('\n')
            : null) ||
          error?.error?.message ||
          error?.message ||
          'No fue posible unir las llaves.';
        void Swal.fire({
          ...this.notification.getSwalBaseConfig(),
          icon: 'error',
          title: 'No se pudo generar el PFX',
          text: String(mensaje),
          confirmButtonText: this.notification.swalConfirmButtonText,
          customClass: this.notification.swalCustomClass
        });
      }
    });
  }

  getFileDetails(files: FileList | null, index: number): void {
    if (!files?.length) {
      return;
    }
    const fileToUpload = files[0];
    const botonActualizado: JoinKeySlot = { ...this.buttons[index] };
    const tamañoEnMegabytes = fileToUpload.size / (1024 * 1024);
    const nameLower = fileToUpload.name.toLocaleLowerCase();
    const okExt =
      (nameLower.endsWith('.key') && botonActualizado.id === 'keyfile') ||
      (nameLower.endsWith('.cer') && botonActualizado.id === 'cerfile');

    if (!okExt) {
      botonActualizado.fondo = '#dc2626';
      botonActualizado.title =
        botonActualizado.id === 'keyfile'
          ? 'Seleccionar archivo de llave privada'
          : 'Seleccionar archivo de certificado';
      botonActualizado.subtitle =
        'El formato de archivo permitido para cargar es ' +
        (botonActualizado.id === 'keyfile' ? '.key' : '.cer');
      this.buttons[index] = botonActualizado;
      this.validateformats[index] = true;
      this.error[index] = botonActualizado.subtitle;
      return;
    }

    if (tamañoEnMegabytes > 10) {
      botonActualizado.fondo = '#dc2626';
      botonActualizado.title = 'Seleccionar archivo';
      botonActualizado.subtitle =
        'El formato de archivo permitido para cargar es ' +
        (botonActualizado.id === 'keyfile' ? '.key' : '.cer');
      this.buttons[index] = botonActualizado;
      this.validateformats[index] = true;
      this.error[index] = 'El archivo supera el tamaño máximo (10 MB).';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result as string;
      botonActualizado.file = b64.split(',')[1];
      this.buttons[index] = { ...this.buttons[index], file: botonActualizado.file };
    };
    reader.readAsDataURL(fileToUpload);

    botonActualizado.fondo = botonActualizado.id === 'keyfile' ? '#6d28d9' : '#0d9488';
    botonActualizado.title = 'Listo';
    botonActualizado.subtitle = fileToUpload.name;
    this.buttons[index] = botonActualizado;
    this.validateformats[index] = false;
    this.error[index] = '';
  }

  descargarPfxGenerado(): void {
    const c = this.certificado;
    if (!c?.certificadoB64) {
      return;
    }
    const fileName = c.nombreArchivo || 'certificado_digital.pfx';
    const base64 = String(c.certificadoB64).replace(/\s/g, '');
    this.dispararDescargaPfxDesdeBase64(base64, fileName);
    this.pfxDescargaDisparada = true;
    this.notification.showDownloadSuccessToast('PFX descargado');
  }

  reiniciarFormulario(): void {
    if (this.panelPfxListo && !this.pfxDescargaDisparada) {
      return;
    }
    this.panelPfxListo = false;
    this.pfxDescargaDisparada = false;
    this.certificado = null;
    this.formaForm.reset();
    this.buttons = [];
    this.validateformats = [];
    this.error = [];
    while (this.documentsDocuments.length) {
      this.documentsDocuments.removeAt(0);
    }
    this.createSlot('Llave privada (.key)', 'keyfile');
    this.createSlot('Llave pública (.cer)', 'cerfile');
  }

  irAFirmar(): void {
    if (this.panelPfxListo && !this.pfxDescargaDisparada) {
      return;
    }
    void this.router.navigate(['/firmar']);
  }

  private createSlot(documentName: string, id: 'keyfile' | 'cerfile'): void {
    this.buttons.push({
      id,
      name: documentName,
      title: id === 'keyfile' ? 'Archivo .key' : 'Archivo .cer',
      subtitle: id === 'keyfile' ? 'Llave privada' : 'Llave pública',
      accept: id === 'keyfile' ? '.key' : '.cer',
      fondo: id === 'keyfile' ? '#5d16a6' : '#0f766e'
    });
    this.documentsDocuments.push(this.fb.control(null));
    this.validateformats.push(false);
    this.error.push('');
  }

  private dispararDescargaPfxDesdeBase64(base64: string, fileName: string): void {
    const mime = 'application/x-pkcs12';
    try {
      const bin = atob(base64);
      const len = bin.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = bin.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 90_000);
    } catch {
      const a = document.createElement('a');
      a.href = `data:${mime};base64,${base64}`;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }
}
